import BlikErrorCode from 'components/blikError/BlikError';
import { PROGRESS } from 'config/config';
import { accordionTransitionTime, blikPlaceholder, PaymentCategoryType } from 'consts';
import { getContainerHeight } from 'helpers/commonHelper';
import React, { createRef, PureComponent, RefObject } from 'react';

import { PaymentDetailsInput, PaymentDetailsWrapper } from './BlikInput.styles';
import withContext from '../../../hoc/withContext';
import withTextContext from '../../../hoc/withTextContext';

interface IProps extends IAccordionWrapper, IContext {
  paymentMethods: IMethod[];
  formData: IFormData;
}

interface IState {
  inputTextValue: string;
  inputValue: string;
  isValid: boolean;
}

interface ITarget {
  target: IEventTarget | HTMLInputElement;
}

interface IEventTarget extends HTMLInputElement {
  value: string;
  placeholder: string;
}

const blikRegex = /[a-z-]/g;
const onlyDigitRegex = /\d{6}/g;

class BlikInput extends PureComponent<IProps, IState> {
  containerRef: RefObject<HTMLDivElement> = createRef();

  blikInput: RefObject<HTMLInputElement> = createRef();

  constructor(props) {
    super(props);
    this.state = {
      inputTextValue: blikPlaceholder,
      inputValue: '',
      isValid: false,
    };
  }

  componentDidMount() {
    const { setHeight } = this.props;
    setHeight(getContainerHeight(this.containerRef.current));

    setTimeout(() => {
      if (this.blikInput.current) {
        this.blikInput.current.focus();
      }
    }, accordionTransitionTime);
  }

  componentDidUpdate() {
    const { setHeight } = this.props;
    setHeight(getContainerHeight(this.containerRef.current));
  }

  handleChange = (event: ITarget) => {
    const {
      target: { value = '' },
    } = event;

    const inputValue = value.replace(/\D/g, '');
    const inputTextValue: string = `${inputValue}${blikPlaceholder}`.slice(0, 6);

    this.setState({ inputValue, inputTextValue }, () => this.validateInput(inputValue));
  };

  validateInput = (value: string) => {
    const { inputValue } = this.state;
    const { setProgress, setFormData } = this.props;
    const clearValue = inputValue.trim().replace(blikRegex, '');
    setFormData({ authorizationCode: clearValue });

    if (clearValue.length !== 6) {
      this.setState({ isValid: false });
    }

    if (value.match(onlyDigitRegex)) {
      this.setState({ isValid: true });
      setProgress(PROGRESS.BLIK_CODE_VERIFICATION);
    } else if (clearValue.length >= 3) {
      setProgress(PROGRESS.BLIK_FILL_3_NUMBERS);
    } else if (clearValue.length < 3) {
      setProgress(PROGRESS.BLIK_CLICK_FIELD);
    }
  };

  handleFocus = () => {
    const { inputValue } = this.state;
    const { setProgress, paymentMethodType } = this.props;
    const clearValue = inputValue.trim().replace(blikRegex, '');
    if (clearValue.length <= 0 && paymentMethodType === PaymentCategoryType.BLIK) {
      setProgress(PROGRESS.BLIK_CLICK_FIELD);
    }
  };

  handleBlur = () => {
    const { inputValue } = this.state;
    const { setProgress } = this.props;
    const clearValue = inputValue.trim().replace(blikRegex, '');
    if (clearValue.length <= 0) {
      setProgress(PROGRESS.SELECT_BLIK_METHOD);
    }
  };

  render() {
    const { isValid, inputValue, inputTextValue } = this.state;
    const { errors } = this.props;

    return (
      <PaymentDetailsWrapper className="blik__container" ref={this.containerRef}>
        <div>
          <PaymentDetailsInput data-testid="blik:wrapper">
            <span
              id="blik-input-text"
              className={`blik__textfield ${inputTextValue === blikPlaceholder ? 'blik__textfield-center' : ''}`}
            >
              {inputTextValue}
            </span>
            <input
              autoComplete="off"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              id="blik-input"
              type="text"
              value={inputValue}
              onChange={this.handleChange}
              className={isValid ? 'success' : ''}
              onFocus={this.handleFocus}
              onBlur={this.handleBlur}
              ref={this.blikInput}
            />
          </PaymentDetailsInput>
          {!!errors.length && <BlikErrorCode errors={errors} />}
        </div>
      </PaymentDetailsWrapper>
    );
  }
}

export default withContext(withTextContext(BlikInput));
